import { jsx as _jsx } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import graphqlClientFactory from 'lib/graphql-client-factory';
import { CurrentPageQuery, NavigationRootQuery } from './ContextualNav.graphql';
import { isValidNavigationItem } from 'lib/utils/navigation-utils';
import ContextualNavClient from './ContextualNavClient';
// Exported for use by ContextualNavItem.tsx — must remain in this file
export const contextualNavTailwindVariants = tv({
    slots: {
        container: [
            'contextual-nav',
            'relative',
            'w-full',
            'min-w-max'
        ],
        heading: [
            'w-full',
            'font-semibold',
            'rounded-general-border-radius-button',
            'text-typography-font-size-body-medium',
            'font-typography-font-family-body',
            'bg-color-fill-brand-1',
            'px-spacing-spacing-4'
        ],
        navContainer: [
            'transition-all',
            'duration-300',
            'ease-in-out'
        ],
        mainNavItem: [
            'relative',
            'group',
            'flex',
            'items-center',
            'transition-colors',
            'duration-200'
        ],
        navItem: [
            'relative',
            'rounded-general-border-radius-button'
        ],
        button: [
            'mx-2',
            'w-8',
            'h-8',
            'flex',
            'items-center',
            'justify-center',
            'rounded-full',
            'hover:bg-color-fill-fill-hover',
            'focus:outline-none',
            'focus:ring-2'
        ],
        mainButton: [
            'ml-auto',
            'w-8',
            'h-8',
            'flex',
            'items-center',
            'justify-center',
            'rounded-border-radius-radius-3',
            'focus:outline-none',
            'focus:ring-border-width-width-05',
            'flex-shrink-0',
            'transition-colors',
            'duration-200'
        ],
        mainLink: [
            '!text-color-text-white',
            'px-spacing-spacing-12',
            'py-spacing-spacing-12',
            'text-color-text-dark'
        ],
        icon: [
            'w-4',
            'h-4',
            'transition-transform',
            'duration-200',
            'flex-shrink-0',
            'text-color-icon-icon',
            'group-hover:text-color-icon-icon-hover'
        ],
        listItem: [
            'py-spacing-spacing-8',
            'border-b',
            'border-color-border-border-secondary'
        ],
        link: [
            'block',
            'font-semibold',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'text-color-text-text-secondary',
            'rounded-general-border-radius-button',
            'hover:text-color-text-text',
            'hover:underline',
            'focus:bg-transparent',
            'focus:outline-offset-0',
            'flex-1',
            'transition-colors',
            'duration-200'
        ],
        list: [
            'rounded-general-border-radius-button',
            'bg-color-surface-white',
            'mx-spacing-spacing-8'
        ],
        sublist: [
            'transition-all',
            'duration-200',
            'ease-in-out'
        ],
        sublistChild: [
            'mt-spacing-spacing-1',
            'mx-spacing-spacing-1',
            'transition-all',
            'duration-200',
            'ease-in-out'
        ],
        childIcon: [
            '!w-6',
            '!h-6',
            'mr-2',
            'transition-opacity',
            'duration-200',
            'ease-in-out',
            'opacity-100',
            'md:opacity-0',
            'md:group-hover:opacity-100',
            'flex-shrink-0',
            'text-color-icon-icon',
            'group-hover:text-color-icon-icon-hover'
        ],
        mainIcon: [
            'mr-spacing-spacing-12',
            'transition-opacity',
            'duration-200',
            'ease-in-out',
            'flex-shrink-0',
            'text-color-text-white',
            'group-hover:text-color-text-white'
        ]
    },
    variants: {
        isExpanded: {
            true: {
                icon: [
                    'rotate-180',
                    'text-color-icon-icon'
                ],
                navItem: [
                    'bg-color-fill-fill-selected',
                    'hover:bg-color-fill-fill-selected'
                ],
                navContainer: [
                    'max-h-full',
                    'opacity-100',
                    'pointer-events-auto'
                ],
                sublist: [
                    'max-h-full',
                    'opacity-100'
                ],
                sublistChild: [
                    'max-h-full',
                    'opacity-100'
                ]
            },
            false: {
                icon: [
                    'text-color-icon-icon'
                ],
                navItem: [
                    'hover:bg-color-fill-fill-hover'
                ],
                navContainer: [
                    'max-h-0',
                    'opacity-0',
                    'pointer-events-none'
                ],
                sublist: [
                    'max-h-0',
                    'opacity-0'
                ],
                sublistChild: [
                    'max-h-0',
                    'opacity-0'
                ]
            }
        },
        current: {
            true: {
                link: [
                    'underline'
                ]
            }
        },
        isChild: {
            true: {
                navItem: [
                    'relative',
                    'my-spacing-spacing-1',
                    'mx-spacing-spacing-1',
                    'group',
                    'flex',
                    'items-center',
                    'transition-colors',
                    'duration-200'
                ],
                link: [
                    'py-spacing-spacing-8',
                    'px-spacing-spacing-12'
                ]
            },
            false: {
                navItem: [
                    'group',
                    'flex',
                    'items-center',
                    'transition-colors',
                    'duration-200'
                ]
            }
        },
        isTopLevel: {
            true: {
                link: [
                    'py-spacing-spacing-8',
                    'px-spacing-spacing-16'
                ],
                childIcon: [
                    'md:opacity-100'
                ]
            }
        },
        isLevel1: {
            true: {
                link: [
                    'pl-spacing-spacing-12'
                ]
            }
        },
        isLevel2: {
            true: {
                link: [
                    'pl-spacing-spacing-32'
                ]
            }
        },
        level2isExpanded: {
            true: {
                navItem: [
                    '!bg-color-fill-fill-secondary',
                    'hover:bg-color-fill-fill-selected'
                ]
            }
        },
        isLevel3: {
            true: {
                link: [
                    'pl-spacing-spacing-48'
                ]
            }
        },
        isChildNoToggle: {
            true: {
                navItem: [
                    'w-fit',
                    '!hover:bg-transparent',
                    '!bg-transparent'
                ]
            },
            false: {
                navItem: [
                    'w-auto'
                ]
            }
        }
    },
    compoundVariants: [
        {
            current: true,
            isChild: true,
            class: {
                navItem: [
                    '!border-b-0',
                    'bg-color-surface-surface-secondary'
                ]
            }
        },
        {
            isChild: true,
            isChildNoToggle: true,
            class: {
                navItem: [
                    'w-fit',
                    'hover:bg-transparent'
                ],
                link: [
                    '!hover:bg-transparent',
                    '!bg-transparent'
                ]
            }
        },
        {
            current: true,
            isLevel2: true,
            isChildNoToggle: true,
            class: {
                navItem: [
                    '!bg-color-surface-white',
                    'hover:bg-color-surface-white'
                ]
            }
        }
    ]
});
function findNavigationRoot(currentPage, ancestors) {
    const editorialAncestors = [
        ...ancestors
    ].reverse();
    for (const ancestor of editorialAncestors){
        if (isValidNavigationItem(ancestor) && ancestor.children?.results?.length > 0) {
            return ancestor.id;
        }
    }
    if (isValidNavigationItem(currentPage) && editorialAncestors.length === 0) {
        return currentPage.id;
    }
    if (editorialAncestors.length > 0 && isValidNavigationItem(editorialAncestors[0])) {
        return editorialAncestors[0].id;
    }
    return null;
}
export const Default = (props)=>/*#__PURE__*/ _jsx(ContextualNavClient, {
        ...props
    });
